import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Select, Space, Table, Typography } from 'antd';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

interface Recipient { user_id: number; user_name: string; email: string; assignment_count: number; ready_count: number }
interface Target { assignment_id?: number; user_id: number; user_name: string; email: string | null; test_title: string | null; reason?: string }
interface Preview { request_id: string | null; items: Target[]; skipped: Target[]; mail_count: number; recipient_count: number }
interface Receipt { request_id: string; queued_count: number; recipient_count: number; skipped: Target[]; replayed: boolean }
interface Props { open: boolean; onClose: () => void; onQueued: (batchId: string) => void }

export default function ManualReminderDialog({ open, onClose, onQueued }: Props) {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState<Preview | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [revision, setRevision] = useState(0);
  const sendBusy = useRef(false);
  const previewAbort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setRecipients([]); setSelected([]); setPlan(null); setReceipt(null); setError(''); setLoading(true);
    api.get<{ items: Recipient[] }>('/admin/mail-reminders/recipients', { signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setRecipients(response.data.items); })
      .catch(reason => { if (!controller.signal.aborted) setError(getApiErrorMessage(reason, 'Не удалось загрузить сотрудников')); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { controller.abort(); previewAbort.current?.abort(); };
  }, [open, revision]);

  const changeSelection = (value: number[]) => {
    previewAbort.current?.abort();
    setPreviewing(false); setSelected(value); setPlan(null); setError('');
  };
  const preview = async () => {
    previewAbort.current?.abort();
    const controller = new AbortController(); previewAbort.current = controller;
    setPreviewing(true); setError(''); setPlan(null);
    try {
      const response = await api.post<Preview>('/admin/mail-reminders/preview', { user_ids: selected }, { signal: controller.signal });
      if (!controller.signal.aborted) setPlan(response.data);
    } catch (reason) {
      if (!controller.signal.aborted) setError(getApiErrorMessage(reason, 'Не удалось проверить получателей'));
    } finally { if (!controller.signal.aborted) setPreviewing(false); }
  };
  const send = async () => {
    if (!plan?.request_id || sendBusy.current) return;
    sendBusy.current = true; setSending(true); setError('');
    try {
      const response = await api.post<Receipt>('/admin/mail-reminders/send', { request_id: plan.request_id });
      setReceipt(response.data);
      if (response.data.queued_count) onQueued(response.data.request_id);
    } catch (reason) {
      setError(getApiErrorMessage(reason, 'Не удалось получить результат отправки. Повторите запрос: эти письма не будут поставлены в очередь дважды.'));
    } finally { sendBusy.current = false; setSending(false); }
  };
  const columns = [
    { title: 'Сотрудник', key: 'user', width: 280, render: (_: unknown, row: Target) => <div className="mail-journal-recipient">
      <strong>{row.user_name}</strong><Typography.Text type="secondary">{row.email}</Typography.Text></div> },
    { title: 'Тест', dataIndex: 'test_title', key: 'test', render: (title: string | null) => title || '—' },
  ];
  const skipped = receipt?.skipped || plan?.skipped || [];
  return <Modal title="Отправить напоминания сейчас" open={open} width={980} onCancel={() => { if (!sending) onClose(); }}
    closable={!sending} maskClosable={!sending} destroyOnHidden
    footer={receipt ? <Button onClick={onClose}>Закрыть</Button> : <Space wrap>
      <Button onClick={onClose} disabled={sending}>Отмена</Button>
      <Button onClick={preview} loading={previewing} disabled={!selected.length || loading || sending}>Проверить получателей</Button>
      <Button type="primary" onClick={send} loading={sending} disabled={!plan?.mail_count || previewing || loading}>
        Отправить письма ({plan?.mail_count || 0})
      </Button>
    </Space>}>
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {receipt ? <Alert showIcon type={receipt.queued_count ? 'success' : 'warning'}
        message={`Писем поставлено в очередь: ${receipt.queued_count}. Получателей: ${receipt.recipient_count}.`}
        description="Результат отправки появится в журнале. Автоматические напоминания продолжатся через 3 рабочих дня после фактической отправки." /> : <>
        <Alert type="info" showIcon message="По одному письму на каждый актуальный незавершённый тест"
          description="Фильтры журнала не ограничивают список. Ручная отправка запускается сразу, в том числе вне рабочего времени. Старые запланированные напоминания будут заменены; следующее — через 3 рабочих дня после отправки." />
        <Select<number[]> mode="multiple" aria-label="Сотрудники для напоминания" placeholder="Выберите сотрудников по ФИО или email"
          style={{ width: '100%' }} loading={loading} value={selected} onChange={changeSelection} disabled={sending}
          optionFilterProp="label" maxTagCount="responsive"
          options={recipients.map(row => ({ value: row.user_id, disabled: !row.ready_count,
            label: `${row.user_name} — ${row.email} · тестов: ${row.ready_count}` }))} />
        <Space wrap>
          <Button size="small" disabled={loading || sending || !recipients.some(row => row.ready_count)}
            onClick={() => changeSelection(recipients.filter(row => row.ready_count).map(row => row.user_id))}>Выбрать всех доступных</Button>
          <Button size="small" disabled={!selected.length || sending} onClick={() => changeSelection([])}>Очистить выбор</Button>
          <Typography.Text type="secondary">Выбрано сотрудников: {selected.length}</Typography.Text>
        </Space>
        {plan && <>
          <Typography.Text strong>Писем к отправке: {plan.mail_count}. Получателей: {plan.recipient_count}.</Typography.Text>
          <Table<Target> size="small" rowKey={row => String(row.assignment_id)} columns={columns} dataSource={plan.items}
            pagination={{ pageSize: 5, showSizeChanger: false }} scroll={{ x: 600 }} />
          <Typography.Text type="secondary">Проверка действительна 10 минут. Письма, отправленные менее 5 минут назад, и отправки с неопределённым результатом будут пропущены.</Typography.Text>
        </>}
        {!loading && !error && !recipients.length && <Alert type="info" message="Нет сотрудников с актуальными незавершёнными тестами" />}
      </>}
      {error && <Alert type="error" showIcon message={error}
        action={!recipients.length && !loading ? <Button onClick={() => setRevision(value => value + 1)}>Повторить загрузку</Button> : undefined} />}
      {skipped.length > 0 && <>
        <Typography.Text strong>{receipt ? 'Пропущены' : 'Будут пропущены'}: {skipped.length}</Typography.Text>
        <Table<Target> size="small" rowKey={row => `${row.user_id}:${row.assignment_id || 'user'}`} dataSource={skipped}
          columns={[...columns, { title: 'Причина', dataIndex: 'reason', key: 'reason' }]}
          pagination={{ pageSize: 3, showSizeChanger: false }} scroll={{ x: 700 }} />
      </>}
    </Space>
  </Modal>;
}
